// filepath: apps/web/src/features/admin/effectsMetrics/EffectsMetricsPage.tsx
//
// FR-UI-ADM-9 — effects operations panel page.
//
// Renders:
//   - <CircuitOpenBanner> for any newly-opened breakers
//   - <MetricsTable> with per-channel metrics + 30s auto-refresh
//   - <CircuitTable> with per-endpoint state + "Configure" action
//   - <CircuitBreakerConfigDialog> for editing BreakerPolicy
//   - AC-5b placeholder card for the deferred 7-day historical view
//
// 5-state fetch contract (FR-UI-CORE-10): loading / error / empty /
// success / 401-re-prompt. 401 clears the operator token.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { useTenant } from "@/tenant/TenantProvider";
import { useOperatorToken } from "@/operator/useOperatorToken";
import { formatDate } from "@/i18n/format";
import { useMetrics, useCircuitSnapshot, useSetBreakerPolicy } from "./queries";
import { MetricsTable } from "./MetricsTable";
import { CircuitTable } from "./CircuitTable";
import { CircuitBreakerConfigDialog } from "./CircuitBreakerConfigDialog";
import { CircuitOpenBanner } from "./CircuitOpenBanner";
import type { CircuitEndpoint, SetBreakerPolicyRequest } from "./types";

export function EffectsMetricsPage(): React.ReactElement {
  const { t, locale } = useTranslation();
  const { tenantSlug } = useTenant();
  const { clear } = useOperatorToken();

  const metrics = useMetrics(tenantSlug);
  const circuit = useCircuitSnapshot(tenantSlug);
  const setPolicy = useSetBreakerPolicy(tenantSlug);

  const [lastRefresh, setLastRefresh] = React.useState<number>(0);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = React.useState<{
    key: string;
    snapshot: CircuitEndpoint;
  } | null>(null);
  // FR-UI-ADM-9 AC-2b — track previously-OPEN endpoints across polls so
  // the banner can fire on a CLOSED → OPEN transition.
  //
  // IMPORTANT: this is a ref, NOT state. A state-based approach
  // would put `previousOpenedKeys` in the effect's dependency array
  // AND set it inside the effect — the React anti-pattern documented
  // by the warning "Maximum update depth exceeded". The ref sidesteps
  // the cycle: mutation happens inside the effect, but the effect
  // does not re-run when the ref contents change.
  const previousOpenedKeysRef = React.useRef<string[]>([]);

  React.useEffect(() => {
    if (circuit.data) setLastRefresh(Date.now());
  }, [circuit.data]);

  React.useEffect(() => {
    if (!circuit.data) return;
    const current = Object.entries(circuit.data.endpoints)
      .filter(([, s]) => s.state === "open")
      .map(([k]) => k);
    const newlyOpened = current.filter(
      (k) => !previousOpenedKeysRef.current.includes(k),
    );
    if (newlyOpened.length > 0) {
      // Banner renders; no further action here.
    }
    previousOpenedKeysRef.current = current;
  }, [circuit.data]);

  React.useEffect(() => {
    if (metrics.error && metrics.error.kind === "error_unauthorized") clear();
    if (circuit.error && circuit.error.kind === "error_unauthorized") clear();
  }, [metrics.error, circuit.error, clear]);

  const openedAtByKey = React.useMemo<Record<string, number>>(() => {
    if (!circuit.data) return {};
    const out: Record<string, number> = {};
    for (const [k, s] of Object.entries(circuit.data.endpoints)) {
      if (s.state === "open") out[k] = s.opened_at_unix_ms;
    }
    return out;
  }, [circuit.data]);

  const isLoading = metrics.isLoading || circuit.isLoading;
  const error = metrics.error ?? circuit.error;
  if (error && error.kind === "error_unauthorized") {
    return (
      <p role="alert" className="effects-metrics-error">
        {t(
          "admin.effects.metrics.error_401",
          "Operator credentials required. Please sign in again.",
        )}
      </p>
    );
  }
  if (isLoading) {
    return (
      <p className="effects-metrics-loading" data-testid="metrics-loading">
        {t("admin.effects.metrics.loading", "Loading effects metrics…")}
      </p>
    );
  }
  if (error) {
    return (
      <p
        role="alert"
        className="effects-metrics-error"
        data-testid="metrics-error"
      >
        {t(
          "admin.effects.metrics.error_fetch",
          "Failed to load effects metrics: {{message}}",
          {
            message: error.message,
          },
        )}
      </p>
    );
  }

  const openedEndpointKeys = Object.entries(circuit.data?.endpoints ?? {})
    .filter(([, s]) => s.state === "open")
    .map(([k]) => k);

  return (
    <div className="effects-metrics-page">
      <CircuitOpenBanner
        tenantSlug={tenantSlug}
        openedEndpointKeys={openedEndpointKeys}
        openedAtByKey={openedAtByKey}
      />

      <header className="effects-metrics-header">
        <h1>{t("admin.effects.metrics.page_title", "Effects Operations")}</h1>
        <div className="effects-metrics-actions">
          <button
            type="button"
            onClick={() => {
              void metrics.refetch();
              void circuit.refetch();
            }}
          >
            {t("admin.effects.metrics.refresh.now", "Refresh now")}
          </button>
          <span
            className="effects-metrics-last-refresh"
            data-testid="last-refresh"
          >
            {lastRefresh > 0
              ? `${t("admin.effects.metrics.last_refreshed_prefix", "Last refreshed:")} ${formatDate(new Date(lastRefresh), locale, { dateStyle: "short", timeStyle: "medium" })}`
              : null}
          </span>
        </div>
      </header>

      <section aria-labelledby="metrics-table-heading">
        <h2 id="metrics-table-heading">
          {t("admin.effects.metrics.section.metrics", "Per-channel metrics")}
        </h2>
        <MetricsTable metrics={metrics.data} />
      </section>

      <section aria-labelledby="circuit-table-heading">
        <h2 id="circuit-table-heading">
          {t(
            "admin.effects.metrics.section.circuit",
            "Per-endpoint circuit state",
          )}
        </h2>
        <CircuitTable
          snapshot={circuit.data}
          onConfigure={(key, snap) => {
            setSelectedEndpoint({ key, snapshot: snap });
            setDialogOpen(true);
          }}
        />
      </section>

      <section
        aria-labelledby="historical-placeholder-heading"
        data-testid="historical-placeholder"
      >
        <h2 id="historical-placeholder-heading">
          {t(
            "admin.effects.metrics.historical_placeholder_title",
            "Historical data (last 7 days)",
          )}
        </h2>
        <p>
          {t(
            "admin.effects.metrics.historical_placeholder_body",
            "Historical data (last 7 days) — coming soon (requires metrics persistence; tracked gap in ARCH §8.4).",
          )}
        </p>
      </section>

      <CircuitBreakerConfigDialog
        open={dialogOpen}
        endpointKey={selectedEndpoint?.key ?? null}
        initial={{
          consecutive_failures: 5,
          open_timeout_ms: 30_000,
          half_open_max_probes: 1,
        }}
        onClose={() => setDialogOpen(false)}
        onSubmit={(req: SetBreakerPolicyRequest) => setPolicy.mutate(req)}
        isPending={setPolicy.isPending}
        error={setPolicy.error}
      />
    </div>
  );
}
